import { BaseService, PagedResult, QueryParams } from '../base/base.service';

/**
 * User entity interface
 */
export interface User {
  id: string;
  userName: string;
  email: string;
  name?: string | null;
  surname?: string | null;
  phoneNumber?: string | null;
  isActive: boolean;
  emailConfirmed: boolean;
  phoneNumberConfirmed: boolean;
  lockoutEnd?: string | null;
  lockoutEnabled: boolean;
  accessFailedCount: number;
  concurrencyStamp?: string | null;
  creationTime: string;
  creatorId?: string | null;
  lastModificationTime?: string | null;
  lastModifierId?: string | null;
  roles?: string[];
  avatar?: string | null;
}

/**
 * Create user request DTO
 */
export interface CreateUserRequest {
  userName: string;
  email: string;
  password: string;
  name: string;
  surname?: string;
  phoneNumber?: string;
  isActive?: boolean;
  lockoutEnabled?: boolean;
  roleNames?: string[];
}

/**
 * Update user request DTO
 */
export interface UpdateUserRequest {
  userName?: string;
  email?: string;
  name?: string;
  surname?: string;
  phoneNumber?: string;
  isActive?: boolean;
  lockoutEnabled?: boolean;
  lockoutEnd?: string | null;
  roleNames?: string[];
  concurrencyStamp?: string;
}

/**
 * Change password request DTO
 */
export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
}

/**
 * Set password request DTO (admin only)
 */
export interface SetPasswordRequest {
  newPassword: string;
}

/**
 * User query parameters
 */
export interface UserQueryParams extends QueryParams {
  roleId?: string;
  isActive?: boolean;
  emailConfirmed?: boolean;
}

/**
 * User activity log entry
 */
export interface UserActivity {
  id: string;
  userId: string;
  action: string;
  description: string;
  ipAddress?: string;
  userAgent?: string;
  timestamp: string;
  metadata?: Record<string, unknown>;
}

/**
 * User Service
 * Provides comprehensive user management functionality for admin panel
 *
 * Features:
 * - User CRUD operations
 * - Role assignment and management
 * - Password management (admin reset)
 * - Account status control (lock/unlock, activate/deactivate)
 * - Activity tracking and audit logs
 * - Advanced search and filtering
 */
export class UserService extends BaseService {
  constructor() {
    super('/api/identity/users');
  }

  /**
   * Get paginated list of users with filtering and sorting
   */
  async getUsers(params: UserQueryParams): Promise<PagedResult<User>> {
    const queryParams = {
      ...this.serializeParams(params),
      ...(params.roleId && { RoleId: params.roleId }),
      ...(params.isActive !== undefined && { IsActive: params.isActive }),
      ...(params.emailConfirmed !== undefined && { EmailConfirmed: params.emailConfirmed }),
    };

    return this.get<PagedResult<User>>('/', { params: queryParams });
  }

  /**
   * Get single user by ID
   */
  async getUser(id: string): Promise<User> {
    return this.get<User>(`/${id}`);
  }

  /**
   * Search users by username, email, or name
   */
  async searchUsers(params: QueryParams): Promise<PagedResult<User>> {
    return this.get<PagedResult<User>>('/', {
      params: this.serializeParams(params),
    });
  }

  /**
   * Create new user
   */
  async createUser(data: CreateUserRequest): Promise<User> {
    const validated = this.validateCreateUserRequest(data);
    return this.post<User, CreateUserRequest>('/', validated);
  }

  /**
   * Update existing user
   */
  async updateUser(id: string, data: UpdateUserRequest): Promise<User> {
    return this.put<User, UpdateUserRequest>(`/${id}`, data);
  }

  /**
   * Delete user
   */
  async deleteUser(id: string): Promise<void> {
    return this.delete<void>(`/${id}`);
  }

  /**
   * Get user roles
   */
  async getUserRoles(id: string): Promise<string[]> {
    try {
      console.log(`Fetching roles for user: ${id}`);
      const response = await this.get<Record<string, unknown>>(`/${id}/roles`);
      console.log('Raw getUserRoles API response:', response);
      
      // Handle different ABP response formats
      // Format 1: { items: [{id, name, isAssigned}, ...] }
      if (response?.items && Array.isArray(response.items)) {
        const firstItem = response.items[0];
        
        // If items contain objects with isAssigned flag
        if (firstItem && typeof firstItem === 'object' && 'isAssigned' in firstItem) {
          const assignedRoles = response.items
            .filter((role: Record<string, unknown>) => role.isAssigned === true)
            .map((role: Record<string, unknown>) => role.name as string);
          console.log('Extracted assigned roles (Format 1):', assignedRoles);
          return assignedRoles;
        }
        
        // If items are strings
        if (typeof firstItem === 'string') {
          console.log('Extracted roles (Format 2):', response.items);
          return response.items as string[];
        }
        
        // If items are role objects without isAssigned, return all names
        if (firstItem && typeof firstItem === 'object' && 'name' in firstItem) {
          const roleNames = response.items.map((role: Record<string, unknown>) => role.name as string);
          console.log('Extracted role names (Format 1b):', roleNames);
          return roleNames;
        }
      }
      
      // Format 3: ["role1", "role2", ...] (direct array)
      if (Array.isArray(response)) {
        console.log('Extracted roles (Format 3):', response);
        return response;
      }
      
      // Format 4: { roleNames: ["role1", "role2", ...] }
      if (response?.roleNames && Array.isArray(response.roleNames)) {
        console.log('Extracted roles (Format 4):', response.roleNames);
        return response.roleNames as string[];
      }
      
      console.warn('Unexpected getUserRoles response format:', response);
      console.warn('Response type:', typeof response);
      console.warn('Response keys:', response ? Object.keys(response) : 'null');
      return [];
    } catch (error) {
      console.error('Error fetching user roles:', error);
      return [];
    }
  }

  /**
   * Update user roles
   * ABP Framework Identity module expects role names in the roleNames property
   */
  async updateUserRoles(id: string, roleNames: string[]): Promise<void> {
    console.log('Updating user roles:', { userId: id, roleNames });
    
    // ABP Identity module expects: { roleNames: ["role1", "role2"] }
    const payload = { roleNames };
    console.log('Sending payload:', payload);
    
    const response = await this.put<void, { roleNames: string[] }>(`/${id}/roles`, payload);
    console.log('Update roles response status:', response);
    return response;
  }

  /**
   * Set user password (admin only - no current password required)
   */
  async setUserPassword(id: string, newPassword: string): Promise<void> {
    return this.post<void, SetPasswordRequest>(`/${id}/set-password`, { newPassword });
  }

  /**
   * Lock user account
   */
  async lockUser(id: string, lockoutEnd?: string): Promise<User> {
    const user = await this.getUser(id);
    const lockoutEndDate = lockoutEnd || new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(); // 1 year from now
    return this.put<User, UpdateUserRequest>(`/${id}`, {
      userName: user.userName,
      email: user.email,
      name: user.name,
      surname: user.surname,
      phoneNumber: user.phoneNumber,
      isActive: user.isActive,
      lockoutEnabled: true,
      lockoutEnd: lockoutEndDate,
      concurrencyStamp: user.concurrencyStamp,
    });
  }

  /**
   * Unlock user account
   */
  async unlockUser(id: string): Promise<User> {
    const user = await this.getUser(id);
    // Set lockoutEnd to a past date to unlock (ABP standard approach)
    const pastDate = new Date(Date.now() - 1000).toISOString(); // 1 second ago
    return this.put<User, UpdateUserRequest>(`/${id}`, {
      userName: user.userName,
      email: user.email,
      name: user.name,
      surname: user.surname,
      phoneNumber: user.phoneNumber,
      isActive: user.isActive,
      lockoutEnabled: user.lockoutEnabled,
      lockoutEnd: pastDate, // Set to past date to clear the lockout
      concurrencyStamp: user.concurrencyStamp,
    });
  }

  /**
   * Activate user account
   */
  async activateUser(id: string): Promise<User> {
    const user = await this.getUser(id);
    return this.put<User, UpdateUserRequest>(`/${id}`, {
      userName: user.userName,
      email: user.email,
      name: user.name,
      surname: user.surname,
      phoneNumber: user.phoneNumber,
      isActive: true,
      lockoutEnabled: user.lockoutEnabled,
      concurrencyStamp: user.concurrencyStamp,
    });
  }

  /**
   * Deactivate user account
   */
  async deactivateUser(id: string): Promise<User> {
    const user = await this.getUser(id);
    return this.put<User, UpdateUserRequest>(`/${id}`, {
      userName: user.userName,
      email: user.email,
      name: user.name,
      surname: user.surname,
      phoneNumber: user.phoneNumber,
      isActive: false,
      lockoutEnabled: user.lockoutEnabled,
      concurrencyStamp: user.concurrencyStamp,
    });
  }

  /**
   * Get user activity log
   */
  async getUserActivity(userId: string, params: QueryParams): Promise<PagedResult<UserActivity>> {
    return this.get<PagedResult<UserActivity>>(`/${userId}/activity`, {
      params: this.serializeParams(params),
    });
  }

  /**
   * Export users to CSV/Excel
   */
  async exportUsers(params: UserQueryParams, format: 'csv' | 'excel' = 'csv'): Promise<Blob> {
    const queryParams = {
      ...this.serializeParams(params),
      format,
    };

    const response = await this.api.get('/export', {
      params: queryParams,
      responseType: 'blob',
    });

    return response.data;
  }

  /**
   * Validate create user request
   */
  private validateCreateUserRequest(data: CreateUserRequest): CreateUserRequest {
    if (!data.userName || data.userName.trim().length === 0) {
      throw new Error('Username is required');
    }

    if (!data.email || !this.isValidEmail(data.email)) {
      throw new Error('Valid email is required');
    }

    if (!data.password || data.password.length < 6) {
      throw new Error('Password must be at least 6 characters');
    }

    if (!data.name || data.name.trim().length === 0) {
      throw new Error('Name is required');
    }

    return data;
  }

  /**
   * Email validation helper
   */
  private isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }
}

// Export singleton instance
export const userService = new UserService();
